import React, { useEffect, useRef, useState } from "react";
import { Swiper as SwiperInstance } from "swiper";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Thumbs } from "swiper/modules";
import { Divider, Image } from "@heroui/react";
import "photoswipe/dist/photoswipe.css";
import { Gallery, Item } from "react-photoswipe-gallery";

// Import Swiper styles
import "swiper/css";
import "swiper/css/free-mode";
import "swiper/css/navigation";
import "swiper/css/thumbs";
import { MdOutlineZoomIn, MdStar } from "react-icons/md";

// Helper function to merge refs
function mergeRefs<T>(...refs: Array<React.Ref<T>>): React.RefCallback<T> {
  return (node: T) => {
    refs.forEach((ref) => {
      if (typeof ref === "function") {
        ref(node);
      } else if (ref) {
        (ref as React.MutableRefObject<T | null>).current = node;
      }
    });
  };
}

// Enhanced Cloudinary URL transformation
function getCloudinaryUrl(
  src: string,
  options: {
    width?: number;
    height?: number;
    quality?: number;
    format?: "auto" | "webp" | "jpg";
    dominantColor?: boolean;
  } = {},
) {
  if (!src.includes("res.cloudinary.com")) return src;

  const { width, height, quality = 80, format = "auto", dominantColor = false } = options;

  const transformations = [
    "f_auto", // Automatic format selection
    "q_auto", // Automatic quality
    "c_fit", // Fit mode to ensure full image is visible
    format !== "auto" ? `f_${format}` : "",
    width ? `w_${width}` : "w_auto",
    height ? `h_${height}` : "",
    quality ? `q_${quality}` : "",
    dominantColor ? "co_rgb:000000,e_colorize:100" : "", // Add color detection
  ]
    .filter(Boolean)
    .join(",");

  return src.replace(/\/upload\//, `/upload/${transformations}/`);
}

// Function to get dominant colors from Cloudinary
// function getDominantColorsUrl(src: string) {
//   if (!src.includes("res.cloudinary.com")) return src;
//   // Get multiple dominant colors
//   return src.replace(
//     /\/upload\//,
//     "/upload/co_rgb:000000,e_colorize:100,co_rgb:000000,e_colorize:100/",
//   );
// }

// Function to calculate multiple dominant colors from image data
// function calculateDominantColors(imageData: ImageData): { primary: string; secondary: string } {
//   const data = imageData.data;
//   const colors: { [key: string]: number } = {};

//   // Sample pixels to get color frequencies
//   for (let i = 0; i < data.length; i += 16) {
//     // Sample every 4th pixel
//     const r = data[i];
//     const g = data[i + 1];
//     const b = data[i + 2];
//     const color = `rgb(${r},${g},${b})`;
//     colors[color] = (colors[color] || 0) + 1;
//   }

//   // Sort colors by frequency
//   const sortedColors = Object.entries(colors)
//     .sort(([, a], [, b]) => b - a)
//     .map(([color]) => color);

//   // Check if there are only two colors and one is white
//   const isWhite = (color: string) => {
//     const rgb = color.match(/\d+/g)?.map(Number) || [0, 0, 0];
//     return rgb[0] > 240 && rgb[1] > 240 && rgb[2] > 240;
//   };

//   const hasOnlyTwoColors = sortedColors.length === 2;
//   const hasWhite = sortedColors.some(isWhite);

//   if (hasOnlyTwoColors && hasWhite) {
//     // If there are only two colors and one is white, use white as the background
//     return {
//       primary: sortedColors[0],
//       secondary: "rgb(255, 255, 255)",
//     };
//   }

//   return {
//     primary: sortedColors[0] || "rgb(248, 248, 248)",
//     secondary: sortedColors[0] || "rgb(248, 248, 248)", // Use primary color as secondary if no second color
//   };
// }

// Function to adjust color brightness for dark/light mode
// function adjustColorForTheme(color: string, isDark: boolean): string {
//   const rgb = color.match(/\d+/g)?.map(Number) || [248, 248, 248];
//   if (isDark) {
//     // Darken the color for dark mode
//     return `rgb(${Math.max(0, rgb[0] - 40)}, ${Math.max(0, rgb[1] - 40)}, ${Math.max(
//       0,
//       rgb[2] - 40,
//     )})`;
//   }
//   // Lighten the color for light mode
//   return `rgb(${Math.min(255, rgb[0] + 20)}, ${Math.min(255, rgb[1] + 20)}, ${Math.min(
//     255,
//     rgb[2] + 20,
//   )})`;
// }

// Simple Tooltip Component
const SimpleTooltip = ({
  position,
  visible,
  children,
}: {
  position: { x: number; y: number };
  visible: boolean;
  children: React.ReactNode;
}) => {
  return (
    <div
      className={`
        fixed z-50 px-3 py-1.5
        bg-blue-600 text-white text-sm
        rounded-md shadow-lg
        transition-opacity duration-200
        pointer-events-none
        ${visible ? "opacity-100" : "opacity-0"}
      `}
      style={{
        left: position.x,
        top: position.y,
        transform: "translate(-50%, -120%)",
      }}
    >
      {children}
    </div>
  );
};

// Function to get image dimensions
const getImageDimensions = (url: string): Promise<{ width: number; height: number }> => {
  return new Promise((resolve) => {
    const img = new window.Image();
    img.onload = () => {
      resolve({
        width: img.naturalWidth,
        height: img.naturalHeight,
      });
    };
    img.onerror = () => {
      // Fallback dimensions if image fails to load
      resolve({ width: 1920, height: 1080 });
    };
    img.src = url;
  });
};

const ProductInfoDisplay = ({
  images,
  title,
  productCode,
  description,
  imageOnly = false,
}: {
  images: Array<string>;
  title: string;
  productCode: string;
  description: string;
  imageOnly?: boolean;
}) => {
  const [thumbsSwiper, setThumbsSwiper] = useState<SwiperInstance | null>(null);
  const [currentSwiper, setSwiper] = useState<SwiperInstance | null>(null);
  const [tooltipState, setTooltipState] = useState({
    visible: false,
    position: { x: 0, y: 0 },
  });
  const [dominantColors, setDominantColors] = useState<{
    [key: string]: { primary: string; secondary: string };
  }>({});
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [imageDimensions, setImageDimensions] = useState<{
    [key: string]: { width: number; height: number };
  }>({});
  const [isNarrowScreen, setIsNarrowScreen] = useState(false);

  useEffect(() => {
    const check = () => setIsNarrowScreen(typeof window !== "undefined" && window.innerWidth < 768);
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);

  // Load image dimensions
  useEffect(() => {
    images.forEach(async (image) => {
      if (!imageDimensions[image]) {
        const dimensions = await getImageDimensions(image);
        setImageDimensions((prev) => ({
          ...prev,
          [image]: dimensions,
        }));
      }
    });
  }, [images]);

  // Function to extract dominant colors from image
  // const extractDominantColors = async (imageUrl: string) => {
  //   try {
  //     const response = await fetch(getDominantColorsUrl(imageUrl));
  //     const blob = await response.blob();
  //     const img = new window.Image();
  //     img.src = URL.createObjectURL(blob);

  //     await new Promise<void>((resolve) => {
  //       img.onload = () => {
  //         const canvas = document.createElement("canvas");
  //         const ctx = canvas.getContext("2d");
  //         if (!ctx) {
  //           resolve();
  //           return;
  //         }

  //         canvas.width = img.width;
  //         canvas.height = img.height;
  //         ctx.drawImage(img, 0, 0);

  //         const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
  //         const colors = calculateDominantColors(imageData);

  //         setDominantColors((prev) => ({
  //           ...prev,
  //           [imageUrl]: colors,
  //         }));

  //         URL.revokeObjectURL(img.src);
  //         resolve();
  //       };
  //     });
  //   } catch (error) {
  //     console.error("Error extracting dominant colors:", error);
  //     setDominantColors((prev) => ({
  //       ...prev,
  //       [imageUrl]: {
  //         primary: "#f8f8f8",
  //         secondary: "#f0f0f0",
  //       },
  //     }));
  //   }
  // };

  // Extract dominant colors for all images
  // useEffect(() => {
  //   images.forEach((image) => {
  //     if (!dominantColors[image]) {
  //       extractDominantColors(image);
  //     }
  //   });
  // }, [images]);

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    setTooltipState({
      visible: true,
      position: { x: e.clientX, y: e.clientY },
    });
  };

  const handleMouseLeave = () => {
    setTooltipState((prev) => ({ ...prev, visible: false }));
  };
  const swiperRef = useRef<SwiperInstance | null>(null);
  const mainSwiperRef = useRef<SwiperInstance | null>(null);

  const navigationPrevRef = React.useRef(null);
  const navigationNextRef = React.useRef(null);

  return (
    <div className={imageOnly ? "w-full" : "grid grid-cols-1 md:grid-cols-2 gap-4 w-full"}>
      {images && (
        <div className="flex flex-col md:flex-row gap-2 w-full">
          <div
            className="product-view relative w-full md:w-24 md:rounded-xl
           overflow-hidden order-2 md:order-1 flex flex-col justify-center items-center"
          >
            <Swiper
              onSwiper={(swiper) => {
                swiperRef.current = swiper;
                setThumbsSwiper(swiper);
              }}
              spaceBetween={10}
              slidesPerView={isNarrowScreen ? 4 : 3}
              freeMode={true}
              navigation={{
                prevEl: navigationPrevRef.current,
                nextEl: navigationNextRef.current,
                enabled: true,
              }}
              direction={isNarrowScreen ? "horizontal" : "vertical"}
              watchSlidesProgress={true}
              modules={[Navigation, Thumbs]}
              className="!min-h-20 md:!min-h-80 max-h-24 md:max-h-32 !py-2"
            >
              {images.map((image, index) => {
                const thumbDimensions = imageDimensions[image] || { width: 80, height: 80 };
                return (
                <SwiperSlide key={image}>
                  <div
                    role="button"
                    tabIndex={0}
                    aria-label={`View image ${index + 1} of ${images.length}`}
                    className="aspect-square bg-zinc-50 border border-zinc-200 dark:border-zinc-600 
                  rounded-md p-0.5 flex items-center justify-center w-20 h-20 md:w-auto md:h-auto cursor-pointer hover:border-primary hover:ring-1 hover:ring-primary transition-colors"
                    onClick={() => mainSwiperRef.current?.slideTo(index)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        mainSwiperRef.current?.slideTo(index);
                      }
                    }}
                  >
                    <Image
                      src={getCloudinaryUrl(image, {
                        width: 80,
                        quality: 70,
                        format: "auto",
                      })}
                      alt={title}
                      width={thumbDimensions.width}
                      height={thumbDimensions.height}
                      className="pointer-events-none max-w-full max-h-full w-auto h-auto"
                      classNames={{
                        wrapper: "!max-w-[none] !w-full !h-full flex items-center justify-center",
                      }}
                    />
                  </div>
                </SwiperSlide>
              );
              })}
            </Swiper>
            <div
              aria-disabled="false"
              ref={navigationNextRef}
              className="swiper-button-next-side !hidden md:!flex border border-transparent !text-primary after:!text-xl after:md:!text-3xl after:!text-primary  after:content-['next']"
              onClick={() => swiperRef.current && swiperRef.current.slideNext()}
            ></div>
            <div
              aria-disabled="false"
              ref={navigationPrevRef}
              className="swiper-button-prev-side  !hidden md:!flex  !text-primary after:!text-xl after:md:!text-3xl after:!text-primary after:content-['prev']"
              onClick={() => swiperRef.current && swiperRef.current.slidePrev()}
            ></div>
          </div>
          <div
            className="product-view relative w-full md:rounded-xl overflow-hidden order-1 md:order-2"
            onMouseMove={handleMouseMove}
            onMouseLeave={handleMouseLeave}
          >
            {!isNarrowScreen && (
              <SimpleTooltip position={tooltipState.position} visible={tooltipState.visible}>
                Click Image to Zoom
              </SimpleTooltip>
            )}
            <Gallery
              withDownloadButton={true}
              options={{
                arrowNext: true,
                arrowKeys: true,
                arrowPrev: true,
                bgClickAction: "close",
                tapAction: "close",
              }}
            >
              <Swiper
                style={
                  {
                    "--swiper-navigation-color": "#fff",
                    "--swiper-pagination-color": "#fff",
                  } as any
                }
                spaceBetween={0}
                slidesPerView={1}
                centeredSlides={true}
                navigation={true}
                onSwiper={(swiper) => {
                  mainSwiperRef.current = swiper;
                  setSwiper(swiper);
                }}
                onSlideChange={(swip) => setSwiper(swip)}
                thumbs={{
                  swiper: thumbsSwiper && !thumbsSwiper?.destroyed ? thumbsSwiper : null,
                }}
                modules={[Navigation, Thumbs]}
                className="product-view-main-swiper relative w-full aspect-square min-h-52 md:min-h-80 max-h-[18rem] md:max-h-[32rem] 2xl:max-h-[35rem] md:rounded-xl overflow-hidden [&_.swiper-slide]:!mr-0"
              >
                {images.map((image, index) => {
                  const dimensions = imageDimensions[image] || { width: 1000, height: 1000 };
                  return (
                    <SwiperSlide key={image} className="h-full !flex items-center justify-center">
                      <Item
                        original={image}
                        thumbnail={image}
                        width={dimensions.width + 50}
                        height={dimensions.height + 50}
                      >
                        {({ ref, open }) => (
                          <div
                            className="relative w-full aspect-square flex items-center justify-center md:rounded-xl overflow-hidden"
                            style={{
                              minHeight: 200,
                              border: isDarkMode ? "#1a1a1a" : "#f8f8f8",
                              backgroundColor: isDarkMode ? "#1a1a1a" : "#f8f8f8",
                              transition: "background-color 0.3s ease",
                            }}
                          >
                            <div className="relative w-full aspect-square flex items-center justify-center max-w-full max-h-full">
                              <div
                                role="button"
                                tabIndex={0}
                                aria-label="Open image zoom gallery"
                                className="z-50 absolute top-2/4 left-2/4 -translate-x-1/2 -translate-y-1/2 bg-orange-600/90 rounded-full p-1 flex items-center justify-center text-white text-center cursor-pointer"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  open(e);
                                }}
                                onKeyDown={(e) => {
                                  if (e.key === "Enter" || e.key === " ") {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    open(e as any);
                                  }
                                }}
                              >
                                <MdOutlineZoomIn className="text-2xl" />
                              </div>
                              <Image
                                src={getCloudinaryUrl(image, {
                                  width: 800,
                                  quality: 85,
                                  format: "auto",
                                })}
                                ref={mergeRefs(ref)}
                                onClick={open}
                                width={1000}
                                height={1000}
                                style={{ aspectRatio: "1 / 1" }}
                                loading="lazy"
                                radius="none"
                                alt={`${title} - image ${index + 1}`}
                                removeWrapper={true}
                                classNames={{
                                  img: `
                                    cursor-pointer 
                                    !transition-opacity 
                                    duration-300 
                                    object-contain
                                    ${
                                      dominantColors[image]?.secondary === "rgb(255, 255, 255)"
                                        ? ""
                                        : "p-2 md:p-6"
                                    }
                                    group-hover:scale-[1.02]
                                    max-w-full
                                    max-h-[16rem] md:max-h-[30rem]
                                    w-auto
                                    h-auto
                                  `,
                                }}
                              />
                            </div>
                          </div>
                        )}
                      </Item>
                    </SwiperSlide>
                  );
                })}
              </Swiper>
            </Gallery>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductInfoDisplay;
